/**
 * SSE streaming API for chat - streams response word by word
 * Fallback to regular POST on failure
 */

const getBaseUrl = () => (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Send message via SSE stream. Calls onToken for each token, onDone when complete.
 * @param {Object} payload - { user_question, roomId?, database_id, module_hint? }
 * @param {Object} callbacks - { onToken, onDone, onError, onCitations, onSuggestions }
 * @returns {Promise<boolean>} - true if streamed, false if fell back to regular fetch
 */
export async function sendStreamMessage(payload, { onToken, onDone, onError, onCitations, onSuggestions }) {
  const baseUrl = getBaseUrl();
  const { user_question, roomId, database_id, module_hint } = payload;
  const url = roomId
    ? `${baseUrl}/chatroom/send/stream/${roomId}/`
    : `${baseUrl}/chatroom/send/stream/`;
  const body = JSON.stringify({
    user_question,
    database_id: database_id || 'Database 1',
    ...(module_hint ? { module_hint } : {}),
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body,
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Stream not supported');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      const lines = buffer.split('\n');
      if (done) {
        buffer = '';
        // Flush all remaining lines when the stream closes
      } else {
        buffer = lines.pop() || '';
      }

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const jsonStr = trimmed.startsWith('data: ')
          ? trimmed.slice(6)
          : trimmed.slice(5);
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.error) {
            onError?.(parsed.error);
            return false;
          }
          if (parsed.type === 'citations') {
            onCitations?.(parsed);
            continue;
          } else if (parsed.type === 'suggestions') {
            if (onSuggestions) onSuggestions(parsed.questions || []);
            continue;
          }
          if (parsed.token && onToken) {
            onToken(parsed.token);
          }
          if (parsed.done) {
            onDone?.(parsed);
            return true;
          }
        } catch (e) {
          console.error('[streamApi] parse error:', e);
        }
      }

      if (done) break;
    }

    return true;
  } catch (err) {
    onError?.(err.message || 'Stream failed');
    return false;
  }
}
